import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, LogOut, Ban, Trash2, XCircle, Loader2, CalendarClock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRIP_LABELS = {
  wreck_day: "Wreck Day",
  tuna_whole: "Tuna Whole Boat",
  tuna_seat: "Tuna Seat",
  all: "All Trips",
};

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const isoDay = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

const authAxios = () => {
  const token = localStorage.getItem("yf_token");
  return axios.create({ headers: { Authorization: `Bearer ${token}` } });
};

export default function Admin() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [smsLog, setSmsLog] = useState([]);
  const [smsMode, setSmsMode] = useState("test");
  const [cursor, setCursor] = useState(() => new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockTrip, setBlockTrip] = useState("all");
  const [blockReason, setBlockReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [tab, setTab] = useState("bookings");
  const [moveId, setMoveId] = useState(null);
  const [moveDate, setMoveDate] = useState("");

  const load = async () => {
    try {
      const ax = authAxios();
      const [b, bl, en, sm] = await Promise.all([
        ax.get(`${API}/admin/bookings`),
        ax.get(`${API}/admin/blocks`),
        ax.get(`${API}/admin/enquiries`),
        ax.get(`${API}/admin/sms-log`),
      ]);
      setBookings(b.data.bookings);
      setBlocks(bl.data.blocks);
      setEnquiries(en.data.enquiries);
      setSmsLog(sm.data.messages);
      setSmsMode(sm.data.mode);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        localStorage.removeItem("yf_token");
        navigate("/admin/login");
      } else {
        setError("Could not load dashboard data.");
      }
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("yf_token")) { navigate("/admin/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayIso = useMemo(() => {
    const n = new Date();
    return isoDay(n.getFullYear(), n.getMonth(), n.getDate());
  }, []);

  const grid = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const lead = (new Date(y, m, 1).getDay() + 6) % 7;
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= new Date(y, m + 1, 0).getDate(); d++) cells.push(isoDay(y, m, d));
    return cells;
  }, [cursor]);

  const blocksOn = (iso) => blocks.filter((b) => b.date === iso);
  const bookingsOn = (iso) => bookings.filter((b) => b.date === iso && b.status !== "cancelled" && b.status !== "expired");

  const addBlock = async () => {
    if (!selectedDate) return;
    setBusy("block");
    setError("");
    try {
      await authAxios().post(`${API}/admin/blocks`, { date: selectedDate, trip_id: blockTrip, reason: blockReason || null });
      setBlockReason("");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not block date");
    }
    setBusy("");
  };

  const removeBlock = async (blockId) => {
    setBusy(blockId);
    try {
      await authAxios().delete(`${API}/admin/blocks/${blockId}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not remove block");
    }
    setBusy("");
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Cancel this booking? If it was paid, a full Stripe refund is issued automatically.")) return;
    setBusy(bookingId);
    setError("");
    try {
      const r = await authAxios().post(`${API}/admin/bookings/${bookingId}/cancel`);
      if (r.data.refunded) alert("Booking cancelled and payment refunded.");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Cancel failed");
    }
    setBusy("");
  };

  const upcoming = bookings.filter((b) => b.date >= todayIso && b.status === "confirmed");

  const reschedule = async (bookingId) => {
    if (!moveDate) { setError("Pick a new date first."); return; }
    setBusy(`move-${bookingId}`);
    setError("");
    try {
      await authAxios().post(`${API}/admin/bookings/${bookingId}/reschedule`, { new_date: moveDate });
      setMoveId(null);
      setMoveDate("");
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Could not move booking");
    }
    setBusy("");
  };
  const other = bookings.filter((b) => !(b.date >= todayIso && b.status === "confirmed"));

  const statusBadge = (s) => ({
    confirmed: "bg-amber-400/20 text-amber-300 border-amber-400/40",
    pending: "bg-cyan-400/10 text-cyan-300 border-cyan-400/30",
    cancelled: "bg-red-400/10 text-red-300 border-red-400/30",
    expired: "bg-stone-700/40 text-stone-400 border-stone-600",
  }[s] || "bg-stone-700/40 text-stone-400 border-stone-600");

  const BookingRow = ({ b }) => (
    <div className="bg-stone-950 border border-stone-800 rounded-xl p-4 flex flex-wrap items-center gap-4 justify-between" data-testid={`booking-row-${b.booking_id}`}>
      <div className="min-w-[140px]">
        <div className="text-white font-bold text-sm">{new Date(b.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" })}</div>
        <div className="text-stone-400 text-xs mt-0.5">{b.trip_name}</div>
      </div>
      <div className="min-w-[140px]">
        <div className="text-stone-200 text-sm font-semibold">{b.name}</div>
        <div className="text-stone-500 text-xs">{b.phone} · {b.email}</div>
      </div>
      <div className="text-stone-300 text-xs">{b.quantity} angler{b.quantity > 1 ? "s" : ""} · <span className="text-amber-400 font-bold">£{Number(b.amount_gbp).toLocaleString("en-GB")}</span></div>
      <span className={`text-[10px] font-mono font-bold uppercase px-2 py-1 rounded border ${statusBadge(b.status)}`}>{b.status}</span>
      {(b.status === "confirmed" || b.status === "pending") && (
        <div className="flex items-center gap-2 flex-wrap">
          {moveId === b.booking_id ? (
            <>
              <input type="date" data-testid={`move-date-${b.booking_id}`} value={moveDate} min={todayIso}
                onChange={(e) => setMoveDate(e.target.value)}
                className="px-3 py-2 bg-stone-900 border border-stone-700 rounded text-white text-xs focus:outline-none focus:border-amber-400" />
              <button data-testid={`move-confirm-${b.booking_id}`} onClick={() => reschedule(b.booking_id)} disabled={busy === `move-${b.booking_id}`}
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-amber-400 hover:bg-amber-300 text-stone-950 rounded transition-colors flex items-center gap-1.5">
                {busy === `move-${b.booking_id}` && <Loader2 className="w-3 h-3 animate-spin" />} Confirm Move
              </button>
              <button data-testid={`move-cancel-${b.booking_id}`} onClick={() => { setMoveId(null); setMoveDate(""); }}
                className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-stone-800 text-stone-300 border border-stone-700 rounded transition-colors">Back</button>
            </>
          ) : (
            <button data-testid={`move-booking-${b.booking_id}`} onClick={() => { setMoveId(b.booking_id); setMoveDate(""); }}
              className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-stone-800 hover:bg-stone-700 text-amber-300 border border-stone-700 hover:border-amber-400/50 rounded transition-colors flex items-center gap-1.5">
              <CalendarClock className="w-3.5 h-3.5" /> Move Date
            </button>
          )}
          <button data-testid={`cancel-booking-${b.booking_id}`} onClick={() => cancelBooking(b.booking_id)} disabled={busy === b.booking_id}
            className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider bg-stone-800 hover:bg-red-950 text-red-300 border border-stone-700 hover:border-red-400/50 rounded transition-colors flex items-center gap-1.5">
            {busy === b.bookingId ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />} Cancel{b.status === "confirmed" ? " & Refund" : ""}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-900 text-stone-100 antialiased" data-testid="admin-dashboard">
      <header className="border-b border-stone-800 bg-stone-950/90 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Yellowfin" className="w-9 h-9 rounded-full border border-stone-700 object-cover" />
            <div>
              <span className="font-bold tracking-tight text-white block text-sm leading-none">YELLOWFIN WHEELHOUSE</span>
              <span className="text-[10px] text-stone-500 uppercase tracking-widest">Skipper Dashboard</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs text-stone-400 hover:text-amber-400" data-testid="admin-view-site-link">View site</Link>
            <button data-testid="admin-logout-btn" onClick={() => { localStorage.removeItem("yf_token"); navigate("/admin/login"); }}
              className="px-4 py-2 text-xs font-bold uppercase tracking-wider bg-stone-800 text-stone-200 border border-stone-700 hover:border-amber-400 rounded transition-colors flex items-center gap-2">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-2 mb-8">
          {[["bookings", "Bookings"], ["calendar", "Calendar & Blocks"], ["sms", "SMS Reminders"], ["enquiries", "Enquiries"]].map(([id, label]) => (
            <button key={id} data-testid={`tab-${id}`} onClick={() => setTab(id)}
              className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider rounded transition-colors ${tab === id ? "bg-amber-400 text-stone-950" : "bg-stone-800 text-stone-300 border border-stone-700 hover:border-amber-400"}`}>
              {label}
            </button>
          ))}
        </div>
        {error && <div className="mb-6 text-xs text-red-400 font-semibold" data-testid="admin-error">{error}</div>}

        {tab === "bookings" && (
          <div className="space-y-6" data-testid="bookings-panel">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Upcoming Confirmed ({upcoming.length})</h2>
              <div className="space-y-3">
                {upcoming.length === 0 && <div className="text-stone-500 text-sm border border-dashed border-stone-800 rounded-xl p-8 text-center">No confirmed bookings yet — share your booking page to fill the calendar.</div>}
                {upcoming.map((b) => <BookingRow key={b.booking_id} b={b} />)}
              </div>
            </div>
            {other.length > 0 && (
              <div>
                <h2 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Past / Pending / Cancelled</h2>
                <div className="space-y-3">{other.map((b) => <BookingRow key={b.booking_id} b={b} />)}</div>
              </div>
            )}
          </div>
        )}

        {tab === "calendar" && (
          <div className="grid lg:grid-cols-12 gap-8" data-testid="calendar-panel">
            <div className="lg:col-span-7">
              <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <button data-testid="admin-cal-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
                    className="p-2 rounded border border-stone-800 hover:border-amber-400 text-stone-300 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
                  <div className="text-white font-bold uppercase tracking-wider text-sm">{cursor.toLocaleString("en-GB", { month: "long", year: "numeric" })}</div>
                  <button data-testid="admin-cal-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
                    className="p-2 rounded border border-stone-800 hover:border-amber-400 text-stone-300 transition-colors"><ChevronRight className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-[11px] font-mono uppercase text-stone-500 mb-2">
                  {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => <div key={d}>{d}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {grid.map((iso, i) => {
                    if (!iso) return <div key={`x${i}`} />;
                    const dayBlocks = blocksOn(iso);
                    const dayBookings = bookingsOn(iso);
                    const sel = selectedDate === iso;
                    return (
                      <button key={iso} data-testid={`admin-day-${iso}`} onClick={() => setSelectedDate(iso)}
                        className={`aspect-square rounded-lg text-sm font-semibold flex flex-col items-center justify-center transition-colors border
                          ${sel ? "bg-amber-400 text-stone-950 border-amber-400" : dayBlocks.length ? "bg-red-950/60 border-red-400/40 text-red-300" : "bg-stone-900 border-stone-800 text-stone-200 hover:border-amber-400"}`}>
                        {parseInt(iso.slice(8), 10)}
                        <div className="flex gap-0.5 mt-0.5">
                          {dayBookings.length > 0 && <span className={`w-1.5 h-1.5 rounded-full ${sel ? "bg-stone-900" : "bg-amber-400"}`}></span>}
                          {dayBlocks.length > 0 && <span className={`w-1.5 h-1.5 rounded-full ${sel ? "bg-stone-900" : "bg-red-400"}`}></span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-4 text-[10px] font-mono text-stone-500">
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-400"></span> has bookings</span>
                  <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-400"></span> blocked</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 space-y-6">
              <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6">
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3 flex items-center gap-2"><Ban className="w-4 h-4 text-red-400" /> Block A Date</h3>
                {selectedDate ? (
                  <>
                    <div className="text-white font-bold text-sm mb-3" data-testid="admin-selected-date">
                      {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    </div>
                    <select data-testid="block-trip-select" value={blockTrip} onChange={(e) => setBlockTrip(e.target.value)}
                      className="w-full px-4 py-3 mb-3 bg-stone-900 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400">
                      <option value="all">All trips (whole boat unavailable)</option>
                      <option value="wreck_day">Wreck Fishing Day only</option>
                      <option value="tuna_whole">Tuna Whole Boat only</option>
                      <option value="tuna_seat">Tuna Individual Seat only</option>
                    </select>
                    <input data-testid="block-reason-input" type="text" placeholder="Reason (e.g. Weather, maintenance)" value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      className="w-full px-4 py-3 mb-3 bg-stone-900 border border-stone-800 rounded-lg text-white text-sm focus:outline-none focus:border-amber-400 placeholder:text-stone-600" />
                    <button data-testid="block-date-btn" onClick={addBlock} disabled={busy === "block"}
                      className="w-full py-3 bg-red-500/90 hover:bg-red-400 text-stone-950 font-black text-xs uppercase tracking-wider rounded-lg transition-colors flex items-center justify-center gap-2">
                      {busy === "block" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Block This Date
                    </button>
                  </>
                ) : (
                  <div className="text-stone-500 text-sm">Click a date on the calendar to block it for weather, maintenance or private use.</div>
                )}
              </div>
              <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6">
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Day Manifest</h3>
                {selectedDate ? (
                  (() => {
                    const dayList = bookingsOn(selectedDate);
                    const seats = dayList.reduce((s, b) => s + b.quantity, 0);
                    return (
                      <>
                        <div className="text-stone-300 text-xs mb-3" data-testid="manifest-summary">
                          {dayList.length === 0
                            ? "No bookings on the selected date."
                            : `${dayList.length} booking(s) · ${seats} angler(s) on ${new Date(selectedDate + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`}
                        </div>
                        <Link to={`/admin/manifest/${selectedDate}`} data-testid="open-manifest-btn"
                          className={`block w-full py-3 text-center font-black text-xs uppercase tracking-wider rounded-lg transition-colors ${dayList.length ? "bg-amber-400 hover:bg-amber-300 text-stone-950" : "bg-stone-800 text-stone-500 border border-stone-700 pointer-events-none"}`}>
                          Open Printable Crew List
                        </Link>
                      </>
                    );
                  })()
                ) : (
                  <div className="text-stone-500 text-sm">Select a date to print its pontoon crew list.</div>
                )}
              </div>
              <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6">
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-3">Active Blocks ({blocks.length})</h3>
                <div className="space-y-2">
                  {blocks.length === 0 && <div className="text-stone-500 text-xs">No blocked dates.</div>}
                  {blocks.map((b) => (
                    <div key={b.block_id} data-testid={`block-row-${b.block_id}`} className="flex items-center justify-between bg-stone-900 border border-stone-800 rounded-lg px-3 py-2">
                      <div className="text-xs">
                        <span className="text-white font-bold">{b.date}</span>
                        <span className="text-stone-400"> · {TRIP_LABELS[b.trip_id] || b.trip_id}{b.reason ? ` · ${b.reason}` : ""}</span>
                      </div>
                      <button data-testid={`unblock-${b.block_id}`} onClick={() => removeBlock(b.block_id)} disabled={busy === b.block_id}
                        className="p-1.5 text-stone-400 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === "sms" && (
          <div className="space-y-4" data-testid="sms-panel">
            <div className="bg-stone-950 border border-stone-800 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="max-w-xl">
                <h3 className="text-xs font-bold uppercase tracking-widest text-stone-400 mb-2">Evening-Before Texts · 6:00pm Daily</h3>
                <p className="text-stone-400 text-xs leading-relaxed">
                  Every evening at 6:00pm, each confirmed angler fishing tomorrow gets a text with the {`7:00am`} meet time and pontoon directions.
                  {smsMode === "test"
                    ? " Currently in TEST MODE — messages are logged below, nothing is sent. Add your Twilio details to go live."
                    : " Live — texts are sent via Twilio."}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-[10px] font-mono font-bold uppercase px-2 py-1 rounded border ${smsMode === "test" ? "bg-cyan-400/10 text-cyan-300 border-cyan-400/30" : "bg-amber-400/20 text-amber-300 border-amber-400/40"}`} data-testid="sms-mode-badge">
                  {smsMode === "test" ? "Test mode" : "Live"}
                </span>
                <button data-testid="sms-run-now-btn" disabled={busy === "sms"} onClick={async () => {
                    setBusy("sms"); setError("");
                    try { await authAxios().post(`${API}/admin/sms-reminders/run`); await load(); }
                    catch (e) { setError(e.response?.data?.detail || "Run failed"); }
                    setBusy("");
                  }}
                  className="px-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-amber-400 hover:bg-amber-300 text-stone-950 rounded transition-colors flex items-center gap-2">
                  {busy === "sms" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Send Tomorrow's Reminders Now
                </button>
              </div>
            </div>
            {smsLog.length === 0 && <div className="text-stone-500 text-sm border border-dashed border-stone-800 rounded-xl p-8 text-center">No reminders logged yet. They appear here each evening, or use the button above to run tomorrow's now.</div>}
            {smsLog.map((m) => (
              <div key={m.message_id} data-testid={`sms-row-${m.message_id}`} className="bg-stone-950 border border-stone-800 rounded-xl p-4">
                <div className="flex justify-between flex-wrap gap-2 items-center">
                  <span className="text-white font-mono text-xs">{m.to}</span>
                  <span className={`text-[10px] font-mono font-bold uppercase px-2 py-1 rounded border ${m.status === "dry_run" ? "bg-cyan-400/10 text-cyan-300 border-cyan-400/30" : m.status === "sent" ? "bg-amber-400/20 text-amber-300 border-amber-400/40" : "bg-red-400/10 text-red-300 border-red-400/30"}`}>
                    {m.status === "dry_run" ? "logged (test)" : m.status}
                  </span>
                </div>
                <div className="text-stone-300 text-xs mt-2 leading-relaxed">{m.body}</div>
                <div className="text-stone-500 text-[10px] mt-2 font-mono">for trip on {m.trip_date} · logged {new Date(m.created_at).toLocaleString("en-GB")}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="space-y-3" data-testid="enquiries-panel">
            {enquiries.length === 0 && <div className="text-stone-500 text-sm border border-dashed border-stone-800 rounded-xl p-8 text-center">No enquiries yet.</div>}
            {enquiries.map((e) => (
              <div key={e.enquiry_id} data-testid={`enquiry-row-${e.enquiry_id}`} className="bg-stone-950 border border-stone-800 rounded-xl p-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <div className="text-white font-bold text-sm">{e.name} <span className="text-stone-500 font-normal">· {e.package}</span></div>
                  <div className="text-stone-500 text-xs">{new Date(e.created_at).toLocaleDateString("en-GB")}</div>
                </div>
                <div className="text-amber-400 text-xs mt-1">{e.contact}</div>
                {e.message && <div className="text-stone-300 text-xs mt-2">{e.message}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
